/**
 * Product detail tabs
 * ---------------------------------------------------------------------------
 * Vertical tabs on desktop, accordion on mobile.
 *
 * Deliberately NOT the CSS-only radio-input pattern. Activating a <label>
 * focuses its radio, and browsers scroll a focused control into view — with
 * the radio absolutely positioned that threw the page down to an empty
 * region. Buttons carry no such behaviour.
 *
 * Panels render visible in the HTML; this hides all but the first once it
 * runs, so the content is fully readable with JavaScript disabled.
 */

const DESKTOP = '(min-width: 900px)';

class ProductTabs {
  constructor(root) {
    this.root = root;
    this.tabs = Array.from(root.querySelectorAll('[data-tab]'));
    this.panels = Array.from(root.querySelectorAll('[data-panel]'));
    this.accordions = Array.from(root.querySelectorAll('[data-acc]'));
    this.query = window.matchMedia(DESKTOP);

    if (!this.panels.length) return;

    this.wireTabs();
    this.wireAccordions();

    this.query.addEventListener
      ? this.query.addEventListener('change', () => this.sync())
      : this.query.addListener(() => this.sync());

    this.select(0, { focus: false });
    this.sync();
  }

  /* ---- Desktop tabs ------------------------------------------------------ */

  wireTabs() {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index, { focus: false }));

      // Roving focus, per the ARIA tabs pattern.
      tab.addEventListener('keydown', (event) => {
        const last = this.tabs.length - 1;
        let next = null;

        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
        else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;

        if (next === null) return;
        event.preventDefault();
        this.select(next, { focus: true });
      });
    });
  }

  select(index, { focus } = {}) {
    this.current = index;

    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      // Only the active tab is tabbable; arrows move between them.
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    });

    // On mobile every panel stays visible and the accordion governs the body,
    // so panel visibility is only toggled on desktop.
    if (this.query.matches) {
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    }
  }

  /* ---- Mobile accordion --------------------------------------------------- */

  wireAccordions() {
    this.accordions.forEach((button, index) => {
      const body = document.getElementById(button.getAttribute('aria-controls'));
      if (!body) return;

      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!open));
        body.hidden = open;
      });
    });
  }

  /** Re-apply the right model when crossing the breakpoint. */
  sync() {
    const desktop = this.query.matches;

    this.panels.forEach((panel, i) => {
      panel.hidden = desktop ? i !== this.current : false;
    });

    this.accordions.forEach((button, i) => {
      const body = document.getElementById(button.getAttribute('aria-controls'));
      if (!body) return;

      if (desktop) {
        // Desktop shows the body outright; the accordion header is hidden.
        body.hidden = false;
        button.setAttribute('aria-expanded', 'true');
      } else {
        const open = i === 0;
        body.hidden = !open;
        button.setAttribute('aria-expanded', String(open));
      }
    });
  }
}

function init() {
  document.querySelectorAll('[data-tabs]').forEach((el) => new ProductTabs(el));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
